import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CmEdObjectCardView} from "../../interface/cm-ed-object-card-view.interface";
import {CmEdSize} from "../../interface/cm-ed-size.type";
import {CmContractExternalDataFoldPt} from "../../interface/cm-contract-external-data-fold-pt.interface";
import {cmEdCountHits, cmEdHasHit} from "../cm-ed-search-hit.util";

/**
 * ONE insured object of the delivery as a card — the house, the vehicle, the piece of property the
 * Deckungen hang off.
 *
 * The overview announces "Objekte N" in its ÜBERBLICK strip, so an object-only delivery used to
 * produce a strip that promised two objects and a body that showed none — the "es wurde nicht
 * importiert" complaint the whole screen exists to end, reproduced by the screen itself. This card is
 * what makes that promise true.
 *
 * Structurally the coverage card minus the two things an object does not have: objects are not grouped
 * by Sparte, so there is nothing to colour by and no accent bar, and the edge between a Deckung and its
 * object is carried by the DECKUNG, so there are no cross-links to render here — an edge stored on both
 * ends would be two things to keep true.
 *
 * <h2>The detail the layout stands or falls on</h2>
 * The heading ELLIPSISES and never wraps. A flex child does not ellipsise without `min-w-0` (its
 * default `min-width: auto` refuses to shrink below its content), which is the single most common way
 * to get this wrong, so the class sits on the heading in the template and is not to be removed.
 *
 * <h2>What it deliberately does NOT do</h2>
 * <ul>
 *   <li>It does not load anything and holds no service. Everything it renders arrives as one input.</li>
 *   <li>It does not FORMAT: every value is printed as the server sent it.</li>
 *   <li>It does not render level 3, for the same reason the coverage card does not: an overview that
 *       can be expanded into the full sheet is not an overview any more, and "Alle Daten" is the one
 *       place that shows everything.</li>
 *   <li>It does not persist its expander. A reader returning to the sheet finds the card in its
 *       resting state, never one that silently hides half its data.</li>
 *   <li>It does not MATCH and it does not HIGHLIGHT a pair. The search runs once in the host; the
 *       card only reveals what a term found and states how much of it there is — see
 *       {@link searching}.</li>
 * </ul>
 */
@Component({
    selector: 'mvs-cm-ed-object-card',
    templateUrl: './cm-ed-object-card.component.html',
    styleUrls: ['./cm-ed-object-card.component.scss'],
    standalone: false,
})
export class CmEdObjectCardComponent implements OnChanges {

    /** Counts every instance ever created, so two cards never share a DOM id. */
    protected static instanceCounter: number = 0;

    /** The object to render. */
    @Input() object: CmEdObjectCardView;

    /** Width bucket, measured once by the host. Decides the type scale and hides the sub line at `xs`. */
    @Input() size: CmEdSize = 'lg';

    /**
     * Whether a search term is currently active — passed through to the pairs, and the switch for the
     * two things the card itself does about a search.
     *
     * It NEVER hides and NEVER dims: an object with no hit renders exactly as it does at rest. What it
     * does instead is state its hits and open itself when the match is behind its expander. The
     * reasoning is written out once, on the coverage card's own `searching`.
     */
    @Input() searching: boolean = false;

    /**
     * THE READER'S OWN expander state — what the card returns to when the term is cleared.
     *
     * Never written by the automatic reveal; see {@link open}.
     */
    expanded: boolean = false;

    /**
     * The reader's decision DURING an active search — `null` while they have not made one.
     *
     * Reader intent wins over the automatic reveal, and is dropped whenever the term or the object
     * changes ({@link ngOnChanges}). Never persisted.
     */
    protected searchOverride: boolean | null = null;

    /**
     * DOM id of the level-2 block, so the expander can point `aria-controls` at what it reveals.
     * Instance-scoped, because a grid renders several of these cards at once.
     */
    protected readonly level2Id: string;

    /** The `aria-expanded` value {@link foldPtCache} currently states; `null` before the first read. */
    private foldPtOpen: boolean | null = null;

    /** The cached pass-through — see {@link foldPt} for why it is cached rather than rebuilt. */
    private foldPtCache: CmContractExternalDataFoldPt | null = null;

    constructor() {
        this.level2Id = 'cmEdObject' + (++CmEdObjectCardComponent.instanceCounter) + '-more';
    }

    /**
     * A card sized to its bucket: tighter padding where the grid puts a card into one column.
     *
     * All six steps written out — the same rule the Deckung and person cards state, so three cards
     * side by side in one grid cannot answer a new step differently.
     */
    get padClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'p-3';
            case 'xs':
                return 'p-2';
        }
    }

    /**
     * Padding of the header BAND — the same horizontal step as {@link padClass} and a smaller
     * vertical one, so the designation shares the left edge of the figures below while the band
     * still reads as a title strip rather than as a second panel.
     *
     * Deliberately identical to the Deckung and Person cards' rule: the three sit in one grid and a
     * band of a different height would make them read as different kinds of thing.
     */
    get headClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'px-3 py-2';
            case 'xs':
                return 'px-2 py-1';
        }
    }

    /**
     * Type scale of the card heading.
     *
     * `text-base` from `sm` up, i.e. from 560 px. The old rule read `xs || sm` and meant "below
     * 600 px"; `sm` now reaches 759 px, where a card is col-12 and therefore the widest card on the
     * ladder.
     */
    get titleClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'text-base';
            case 'xs':
                return 'text-sm';
        }
    }

    /**
     * True while a term is active AND this object's Weitere Angaben hold one of its matches.
     *
     * Aggregates the flag the HOST set on each field; it folds nothing and compares nothing — the
     * sheet has exactly one matcher (`cm-ed-search-hit.util.ts`).
     */
    get level2Hit(): boolean {
        return this.searching && cmEdHasHit(this.object?.level2);
    }

    /**
     * True when the term matched the object's own TITLE or its sub line rather than one of its pairs —
     * the switch for the mark on the heading.
     *
     * The designation is part of the host's haystack and is counted into
     * {@link CmEdObjectCardView#hitCount}; nothing on the card used to show it, so the "{n} Treffer"
     * marker went up and no text changed. A count that points at nothing is exactly what this sheet may
     * not do.
     *
     * Read by SUBTRACTION from the flags the host wrote — the card's total minus the hits its rendered
     * pairs account for — because the sheet has ONE matcher and this component neither sees the term
     * nor ever may. Exact in every combination, since the host builds the total by addition from the
     * same parts: `countTextHits(title, sub) + fieldHits(level1) + fieldHits(level2)`.
     *
     * It says WHICH HEADING, never which characters: the host splits labels and values into highlight
     * runs and splits no title. Segments for the title and the sub would make it exact, and that is a
     * host change.
     */
    get ownTextHit(): boolean {

        if (!this.searching || !this.object) {
            return false;
        }

        const pairs = cmEdCountHits(this.object.level1) + cmEdCountHits(this.object.level2);

        return this.object.hitCount > pairs;
    }

    /**
     * Whether the level-2 block is on screen — the reader's own state, temporarily overruled by a
     * search that found something behind the expander.
     *
     * An OVERLAY and never a write, so clearing the term returns the card to exactly the state its
     * reader left it in; a click always wins. The full reasoning is on the coverage card's `open`.
     */
    get open(): boolean {

        if (!this.searching) {
            return this.expanded;
        }

        return this.searchOverride ?? (this.expanded || this.level2Hit);
    }

    /**
     * The expander's `aria-expanded` / `aria-controls`; see {@link CmContractExternalDataFoldPt}.
     *
     * Cached on the value it states, so the object identity `[pt]` watches changes exactly when
     * {@link open} does — the reader's click, the search flipping, and the host recomputing hits on
     * the same object are three separate causes, and rebuilding on toggle alone covered one.
     */
    protected get foldPt(): CmContractExternalDataFoldPt {

        const open = this.open;

        if (this.foldPtOpen !== open || !this.foldPtCache) {
            this.foldPtOpen = open;
            this.foldPtCache = {
                root: {
                    'aria-expanded': String(open),
                    'aria-controls': this.level2Id,
                },
            };
        }

        return this.foldPtCache;
    }

    /**
     * Drops the reader's search-time override whenever the question changes — a new term, a search
     * switched on or off, or a new object. {@link expanded} is deliberately left alone.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes["searching"] || changes["object"]) {
            this.searchOverride = null;
        }
    }

    /**
     * Opens / closes the level-2 block.
     *
     * While a term is active the click is recorded as an OVERRIDE rather than as the reader's resting
     * state, so a card the search opened can be closed and a card closed while searching does not stay
     * shut once the term is cleared.
     */
    toggle(): void {

        if (this.searching) {
            this.searchOverride = !this.open;
            return;
        }

        this.expanded = !this.expanded;
    }
}
